import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { Button, Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import ChatIcon from 'icons/Chat.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import useCallbackRef from 'hooks/useCallbackRef';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { getShowGroupChat } from 'utils';
import useGroupChatBuySideDiscovery from 'router/subrouters/DataRoom/hooks/useGroupChatBuySideDiscovery';

import './MessageSellSideButton.styles.scss';

type MessageSellSideButtonProps = {
  className?: string;
};

const MessageSellSideButton: React.FC<MessageSellSideButtonProps> = (props) => {
  const { className } = props;

  const {
    isBuySide,
    dataRoom: {
      currentMember: { sessionId },
    },
    openChatDrawer,
  } = useContext(DataRoomContext);
  const { buySideGroups } = useContext(MembersContext);

  const { shouldHighlightGroupChat, markDrawerOpened } =
    useGroupChatBuySideDiscovery();

  const { activeChatSessionId, dataRoomMembers } = buySideGroups?.[0] || {};

  const showGroupChat = useMemo(
    () => getShowGroupChat(activeChatSessionId, dataRoomMembers),
    [dataRoomMembers, activeChatSessionId],
  );

  const isPhablet = useMatchMedia('isPhablet');

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const canMessageSellSide = useMemo(
    () => isBuySide && sessionId,
    [isBuySide, sessionId],
  );

  const classes = classNames('message-sell-side-button', className, {
    'new-feature': showGroupChat && shouldHighlightGroupChat,
  });

  const { t } = useTranslation();

  const openChat = useCallback(
    (chatSessionId: string, groupChatSessionId?: string) => {
      openChatDrawer(chatSessionId, groupChatSessionId);
      dropdown?.setIsOpen(false);
    },
    [dropdown, openChatDrawer],
  );

  if (!canMessageSellSide) return null;

  const trigger = (
    <Button className={classes} variant="outline" icon={ChatIcon}>
      {t('messageSellSide')}
    </Button>
  );

  if (!showGroupChat)
    return React.cloneElement(trigger, {
      onClick: () => openChatDrawer(sessionId),
    });

  return (
    <Dropdown
      ref={dropdownRef}
      renderAsPortal={!isPhablet}
      portalClassName="message-sell-side-button__menu"
      trigger={trigger}
    >
      <ul className="message-sell-side-button__options">
        <li>
          <Button
            variant="link"
            onClick={() => openChat(sessionId, activeChatSessionId)}
          >
            {t('privateChat')}
          </Button>
        </li>
        <li>
          <Button
            variant="link"
            className={classNames({ 'new-feature': shouldHighlightGroupChat })}
            onClick={() => {
              markDrawerOpened();
              openChat(activeChatSessionId);
            }}
          >
            {t('groupChat')}
          </Button>
        </li>
      </ul>
    </Dropdown>
  );
};

export default MessageSellSideButton;
